import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import apiServices from "../../../../services/apiService";
import MetricCard from "../../../common/Card/MetricCard";
import Chart from "react-apexcharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faBuilding, faDoorClosed, faClock, faWrench, faBullhorn, faUserPlus, faClipboardList, faCalendarCheck, faTicketAlt } from "@fortawesome/free-solid-svg-icons";
import storageService from "../../../../utils/storageService";
import encryptedDataService from "../../../../services/encryptedDataService";
import { primaryColor, secondaryColor } from "../../../../utils/comon";
import ActionCenter from "../../../common/ActionCenter/ActionCenter";

const Dashboard = () => {
    const { profileData } = useSelector((state) => state.profile);
    
    const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
    useEffect(() => {
        const handleThemeChange = () => {
            setTheme(localStorage.getItem("theme") || "light");
        };
        window.addEventListener("themeChanged", handleThemeChange);
        return () => window.removeEventListener("themeChanged", handleThemeChange);
    }, []);
    const isDark = theme === "dark";

    const [dashboardData, setDashboardData] = useState({
        userData: [],
        masters: {},
        users: {},
        visitors: {},
        notifications: {}
    });

    const [wingStats, setWingStats] = useState({});
    const [wings, setWings] = useState([]);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const encryptedEmployeeCode = storageService.getItem('employeeCode');
                if (!encryptedEmployeeCode) return;
                
                const userId = encryptedDataService.decryptData(encryptedEmployeeCode);
                
                // Step 1: Get user site
                const userRes = await apiServices({
                    method: 'GET', url: `/Residential/ResUserDetails/getUserDetails?Id=${userId}`
                });

                if (userRes?.success && userRes?.data?.data?.length > 0) {
                    const userData = userRes.data.data[0];
                    const siteId = userData.siteId;
                    const roleId = userData.roleId || 4;
                    const unitId = userData.unitId || 1;

                    // Step 2: Get Dashboard Data (Site-wide)
                    const dashRes = await apiServices({
                        method: "GET",
                        url: `/Residential/ResDashboard/GetDashboardSummary?siteId=${siteId}&roleId=${roleId}`
                    });

                    if (dashRes?.success && dashRes?.data?.isSuccess && dashRes?.data?.data) {
                        const payload = dashRes.data.data;
                        setDashboardData(payload);

                        // Step 3: Fetch per-wing stats
                        const allWingsMap = new Map();
                        payload.userData?.forEach(u => {
                            u.wingDetails?.list?.forEach(w => {
                                if (!allWingsMap.has(w.id)) {
                                    allWingsMap.set(w.id, w);
                                }
                            });
                        });
                        const wingsArray = Array.from(allWingsMap.values());
                        setWings(wingsArray);
                        
                        if (wingsArray.length > 0) {
                            const wingPromises = wingsArray.map(wing => 
                                apiServices({
                                    method: "GET",
                                    url: `/Residential/ResDashboard/GetDashboardSummary?siteId=${siteId}&roleId=${roleId}&wingId=${wing.id}`
                                })
                            );

                            const wingResponses = await Promise.all(wingPromises);
                            const newWingStats = {};
                            wingResponses.forEach((res, index) => {
                                if (res?.success && res?.data?.isSuccess && res?.data?.data) {
                                    newWingStats[wingsArray[index].id] = res.data.data;
                                }
                            });
                            setWingStats(newWingStats);
                        }
                    }
                }
            } catch (error) {
                console.error("Dashboard fetch error:", error);
            }
        };

        fetchDashboardData();
    }, []);

    // ----------------------------------------------------
    // METRICS CALCULATION
    // ----------------------------------------------------
    const { userData, masters, users, visitors } = dashboardData;

    const totalBuildings = masters?.totalWings || 0;
    const totalUnits = masters?.totalUnits || 0;
    const totalUsers = users?.totalUsers || 0;
    const todaysVisitors = visitors?.totalVisitors || 0;

    const topMetrics = [
        {
            title: "Total Buildings",
            value: totalBuildings,
            icon: faBuilding,
            color: "text-red-500"
        },
        {
            title: "Total Units",
            value: totalUnits,
            icon: faDoorClosed,
            color: "text-blue-500"
        },
        {
            title: "Total Users",
            value: totalUsers,
            icon: faUsers,
            color: "text-green-500"
        },
        {
            title: "Today's Visitors",
            value: todaysVisitors,
            icon: faUsers,
            color: "text-purple-500"
        }
    ];

    // ----------------------------------------------------
    // SITE CONTEXT
    // ----------------------------------------------------
    const siteContext = {
        siteName: userData?.[0]?.siteName || "Your Site",
        cityName: userData?.[0]?.cityName || "City",
        adminFirstName: userData?.[0]?.userName || "Site Admin",
        adminContact: userData?.[0]?.mobileNumber || "N/A",
        adminEmail: userData?.[0]?.email || "N/A"
    };

    // ----------------------------------------------------
    // CHARTS CONFIGURATION
    // ----------------------------------------------------

    // 1. Unit Status (Column Chart)
    const buildingCategories = ['Vacant', 'Owned', 'Occupied', 'Rented'];
    const buildingUnitsData = [masters?.vacantUnits || 0, masters?.ownedUnits || 0, masters?.occupiedUnits || 0, masters?.rentedUnits || 0];

    const buildingChartOptions = {
        chart: { type: "bar", toolbar: { show: false } },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '45%',
                borderRadius: 4
            }
        },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 2, colors: ['transparent'] },
        colors: [primaryColor()],
        xaxis: {
            categories: buildingCategories,
            labels: {
                style: {
                    colors: isDark ? "#FFFFFF" : "#64748B"
                }
            }
        },
        yaxis: {
            labels: {
                style: {
                    colors: isDark ? "#FFFFFF" : "#64748B"
                }
            }
        },
        legend: {
            position: "top",
            horizontalAlign: "left",
            labels: {
                colors: isDark ? "#FFFFFF" : "#64748B"
            }
        },
        fill: { opacity: 1 },
        tooltip: {
            y: { formatter: (val) => val }
        }
    };

    const buildingChartSeries = [
        { name: "Units", data: buildingUnitsData }
    ];

    // 2. Unit Usage Analytics (Donut Chart)
    const donutChartOptions = {
        chart: { type: "donut" },
        labels: ["Invited Visitors", "Walk-in Visitors"],
        colors: [primaryColor(), secondaryColor()],
        plotOptions: {
            pie: {
                donut: {
                    size: '75%',
                    labels: {
                        show: true,
                        name: {
                            show: true,
                            color: isDark ? '#94A3B8' : '#64748B',
                        },
                        value: {
                            show: true,
                            fontSize: '24px',
                            fontWeight: 'bold',
                            color: isDark ? '#FFFFFF' : '#0F172A',
                        },
                        total: {
                            show: true,
                            showAlways: true,
                            label: 'TOTAL',
                            color: isDark ? '#94A3B8' : '#64748B',
                            formatter: function (w) {
                                return w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                            }
                        }
                    }
                }
            }
        },
        dataLabels: { enabled: false },
        legend: {
            position: "bottom",
            labels: {
                colors: isDark ? "#FFFFFF" : "#64748B"
            }
        },
    };

    const donutChartSeries = [visitors?.invitedVisitors || 0, visitors?.nonInvitedVisitors || 0];

    // 3. Visitor Analytics (Line Chart)
    const visitorCategories = ['Approved', 'Pending', 'Checked In', 'Checked Out'];

    const visitorChartOptions = {
        chart: { type: "bar", toolbar: { show: false } },
        plotOptions: {
            bar: { horizontal: false, columnWidth: '45%', borderRadius: 4 }
        },
        colors: [secondaryColor()],
        xaxis: {
            categories: visitorCategories,
            labels: {
                style: {
                    colors: isDark ? "#FFFFFF" : "#64748B"
                }
            }
        },
        yaxis: {
            labels: {
                style: {
                    colors: isDark ? "#FFFFFF" : "#64748B"
                }
            }
        },
        legend: {
            position: "top",
            horizontalAlign: "left",
            labels: {
                colors: isDark ? "#FFFFFF" : "#64748B"
            }
        },
        dataLabels: { enabled: false }
    };

    const visitorChartSeries = [
        { name: "Visitors", data: [visitors?.approvedVisitors || 0, visitors?.pendingVisitors || 0, visitors?.checkedInVisitors || 0, visitors?.checkedOutVisitors || 0] }
    ];

    const dashboardActions = [
        {
            icon: faBullhorn,
            title: "Notices",
            description: "Manage community notices",
            url: "/zentres/Notices",
            iconStyles: { bg: "bg-indigo-100", color: "text-indigo-600" }
        },
        {
            icon: faClipboardList,
            title: "Polls",
            description: "Create and view community polls",
            url: "/zentres/Poll-Management",
            iconStyles: { bg: "bg-blue-100", color: "text-blue-600" }
        },
        {
            icon: faCalendarCheck,
            title: "Amenities",
            description: "Check and manage amenity bookings",
            url: "/zentres/amenities-management",
            iconStyles: { bg: "bg-green-100", color: "text-green-600" }
        },
        {
            icon: faTicketAlt,
            title: "Complaints",
            description: "Resolve helpdesk issues",
            url: "/zentres/ComplaintList",
            iconStyles: { bg: "bg-red-100", color: "text-red-600" }
        }
    ];

    const currentDate = new Date();
    const currentHour = currentDate.getHours();
    const greeting = currentHour >= 5 && currentHour < 12 ? "Good Morning" : currentHour >= 12 && currentHour < 17 ? "Good Afternoon" : currentHour >= 17 && currentHour < 21 ? "Good Evening" : "Good Night";

    const formattedDate = currentDate.toLocaleString("en-US", { weekday: "long", day: "2-digit", month: "short", year: "numeric" });

    return (
        <div className="flex flex-col h-full overflow-hidden bg-dashboardbg">
            <div className="flex-1 overflow-y-auto p-4 lg:p-8 custom-scrollbar">
                <div className="max-w-[1600px] mx-auto w-full">

                    {/* Header */}
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-textprimary mb-1">
                            {greeting}, {profileData?.firstName ? `${profileData.firstName} ${profileData.lastName}` : (userData?.[0]?.userName || "User")}! 👋
                        </h1>
                        <p className="text-sm text-textsecondary">
                            {formattedDate} • Here is your site overview
                        </p>
                    </div>

                    {/* Top Metric Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                        {topMetrics.map((metric, idx) => (
                            <MetricCard key={idx} {...metric} />
                        ))}
                    </div>

                    {/* Enterprise Plan (Site Context) */}
                    {siteContext && (
                        <div className="bg-cardbg rounded-xl shadow-sm border border-bordercolor p-3 mb-6 flex flex-col md:flex-row items-center justify-between text-sm">
                            <div className="flex items-center gap-6">
                                <div className="w-14 h-14 bg-secondaryaccent text-primaryaccent border border-accentborder rounded-xl flex flex-col items-center justify-center font-bold text-xs">
                                    Logo
                                </div>
                                <div>
                                    <h2 className="text-sm font-bold text-textprimary flex items-center gap-2">
                                        {siteContext.siteName} <span className="text-red-500 text-sm">— Active</span>
                                    </h2>
                                    <div className="flex gap-8 mt-3 text-xs">
                                        <div>
                                            <p className="text-textsecondary font-medium text-[10px] mb-1 uppercase">Location</p>
                                            <p className="font-semibold text-textprimary">{siteContext.cityName}</p>
                                        </div>
                                        <div>
                                            <p className="text-textsecondary font-medium text-[10px] mb-1 uppercase">Site Admin</p>
                                            <p className="font-semibold text-textprimary">{siteContext.adminFirstName}</p>
                                        </div>
                                        <div>
                                            <p className="text-textsecondary font-medium text-[10px] mb-1 uppercase">Contact</p>
                                            <p className="font-semibold text-textprimary">{siteContext.adminContact}</p>
                                        </div>
                                        <div>
                                            <p className="text-textsecondary font-medium text-[10px] mb-1 uppercase">Email</p>
                                            <p className="font-semibold text-textprimary">{siteContext.adminEmail}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Row 1.5: Wing Details Cards */}
                    {wings.length > 0 && (
                        <div className="flex gap-6 mb-6">
                            {wings.map((wing, idx) => (
                                <div key={idx} className="w-full bg-cardbg rounded-xl shadow-sm border border-bordercolor p-3">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-secondaryaccent text-primaryaccent border border-accentborder rounded-lg flex items-center justify-center">
                                                <FontAwesomeIcon icon={faBuilding} className="text-lg" />
                                            </div>
                                            <div>
                                                <h4 className="text-sm font-bold text-textprimary">{wing.wingName || "Wing"}</h4>
                                                <p className="text-xs text-textsecondary">Wing</p>
                                            </div>
                                        </div>
                                        <span className="bg-green-50 text-green-600 text-[10px] px-2 py-1 rounded font-semibold">Active</span>
                                    </div>
                                    <div className="flex justify-around items-center border-t border-bordercolor pt-2">
                                        <div className="text-center">
                                            <p className="text-base font-bold text-textprimary">{wingStats[wing.id]?.masters?.totalUnits ?? "-"}</p>
                                            <p className="text-[10px] text-textsecondary font-semibold uppercase">Total Units</p>
                                        </div>
                                        <div className="text-center">
                                            <p className="text-base font-bold text-textprimary">{wingStats[wing.id]?.users?.totalUsers ?? "-"}</p>
                                            <p className="text-[10px] text-textsecondary font-semibold uppercase">Users</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Row 2: Building Occupancy & Unit Analytics */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                        {/* Building Occupancy */}
                        <div className="lg:col-span-2 bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5">
                            <h3 className="text-base font-bold text-textprimary mb-1">Unit Status Overview</h3>
                            <p className="text-xs text-textsecondary mb-4">Distribution of units</p>
                            <div className="h-[300px]">
                                <Chart options={buildingChartOptions} series={buildingChartSeries} type="bar" height="100%" />
                            </div>
                        </div>

                        {/* Quick Access */}
                        <ActionCenter
                            title="Quick Access"
                            description="Quick shortcuts to key management workflows."
                            actions={dashboardActions}
                        />
                    </div>

                    {/* Row 3: Visitor Analytics & Quick Access */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Visitor Line Chart */}
                        <div className="lg:col-span-2 bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5">
                            <div className="flex justify-between items-center mb-1">
                                <h3 className="text-base font-bold text-textprimary">Visitor Status Overview</h3>
                                <span className="bg-red-50 text-red-500 text-[10px] px-2 py-1 rounded font-semibold flex items-center gap-1">
                                    <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div> Live
                                </span>
                            </div>
                            <p className="text-xs text-textsecondary mb-4">Current status of visitors</p>
                            <div className="h-[300px]">
                                <Chart options={visitorChartOptions} series={visitorChartSeries} type="bar" height="100%" />
                            </div>
                        </div>

                        {/* Unit Analytics (Donut) */}
                        <div className="bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5">
                            <h3 className="text-base font-bold text-textprimary mb-1">Visitor analytics</h3>
                            <p className="text-xs text-textsecondary mb-4">Total visitors breakdown</p>
                            <div className="h-[300px] flex items-center justify-center">
                                <Chart options={donutChartOptions} series={donutChartSeries} type="donut" height="100%" />
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default Dashboard;
